import FileDownload from 'js-file-download';
import { useState } from 'react';

import { Button } from '../../../../cdl/Button/Button';
import { IconButton } from '../../../../cdl/IconButton/IconButton';
import { Modal } from '../../../../cdl/Modal/Modal';
import { ReadOnlyField } from '../../../../cdl/ReadOnlyField/ReadOnlyField';
import { Text } from '../../../../cdl/Text/Text';
import { ButtonGroup } from '../../../../common/buttons/ButtonGroup';
import { translate } from '../../../../common/helpers/translate.helper';
import { useCopyToClipboard } from '../../../../common/hooks/useCopyToClipboard';
import { IconClipboardText } from '../../../../common/icons/cdl/ClipboardText';
import { IconDownload } from '../../../../common/icons/cdl/Download';
import { Box } from '../../../../common/ui/Box';
import { useRecoveryCodeGenerate } from '../../hooks/useRecoveryCodeGenerate';

interface BackupCodeModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const BackupCodeModal = ({ isOpen, onClose }: BackupCodeModalProps) => {
    const [backupCode, setBackupCode] = useState<string>('');
    const [isGenerated, setIsGenerated] = useState(false);

    const generateCodeMutation = useRecoveryCodeGenerate();
    const copyToClipboard = useCopyToClipboard();

    const handleGenerateCode = async () => {
        try {
            const response = await generateCodeMutation.mutateAsync();
            setBackupCode(response.recoveryCode);
            setIsGenerated(true);
        } catch {
            onClose();
        }
    };

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            setBackupCode('');
            setIsGenerated(false);
        }, 1000);
    };

    const handleDownload = () => {
        FileDownload(backupCode, 'Closelink Backup Code.txt', 'text/plain');
    };

    const isLoading = generateCodeMutation.isPending;

    return (
        <Modal isOpen={isOpen} onDismiss={handleClose} size="small" dismissible={false}>
            <Box padding={24} display="grid" gap={16}>
                {!isGenerated ? (
                    <>
                        <Text variant="title" as="h1">
                            {translate('mfa.backupCode.generateTitle')}
                        </Text>

                        <Text variant="small">{translate('mfa.backupCode.generateDescription')}</Text>

                        <ButtonGroup justifyContent="end">
                            <Button onClick={handleClose} disabled={isLoading}>
                                {translate('btn.cancel')}
                            </Button>
                            <Button
                                emphasis="high"
                                destructive={true}
                                onClick={handleGenerateCode}
                                isLoading={isLoading}
                            >
                                {translate('mfa.backupCode.btn.generate')}
                            </Button>
                        </ButtonGroup>
                    </>
                ) : (
                    <>
                        <Text variant="title" as="h1">
                            {translate('mfa.backupCode.displayTitle')}
                        </Text>

                        <Text variant="small">{translate('mfa.backupCode.displayDescription')}</Text>

                        <ReadOnlyField
                            value={backupCode}
                            trailingVisual={
                                <IconButton
                                    onClick={() => copyToClipboard(backupCode)}
                                    Icon={IconClipboardText}
                                    tooltipLabel={translate('mfa.backupCode.btn.copy')}
                                />
                            }
                        />

                        <Box>
                            <Button onClick={handleDownload} LeadingIcon={IconDownload}>
                                {translate('mfa.backupCode.btn.download')}
                            </Button>
                        </Box>

                        <ButtonGroup justifyContent="end">
                            <Button emphasis="high" onClick={handleClose}>
                                {translate('mfa.backupCode.btn.saved')}
                            </Button>
                        </ButtonGroup>
                    </>
                )}
            </Box>
        </Modal>
    );
};
